import { MutationCache, QueryCache, QueryClient, type Mutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  trackOutcome,
  type AnalyticsEventParametersFor,
  type AnalyticsOutcome,
  type AnalyticsOutcomeEventName,
} from '@/lib/analytics'
import { getApiErrorMessage } from '@/lib/apiErrors'
import { useStationStore } from '@/lib/stores/station'

type MutationEventParameters<TEvent extends AnalyticsOutcomeEventName> =
  TEvent extends AnalyticsOutcomeEventName
    ? Omit<AnalyticsEventParametersFor<TEvent>, 'area' | 'outcome'>
    : never
type MutationAnalytics<
  TVariables = unknown,
  TEvent extends AnalyticsOutcomeEventName = AnalyticsOutcomeEventName,
> = {
  event: TEvent | ((variables: TVariables) => TEvent)
  area: AnalyticsEventParametersFor<TEvent>['area']
  parameters?:
    | MutationEventParameters<TEvent>
    | ((variables: TVariables) => MutationEventParameters<TEvent>)
}

export const defineMutationAnalytics = <
  TVariables,
  TEvent extends AnalyticsOutcomeEventName = AnalyticsOutcomeEventName,
>(
  analytics: MutationAnalytics<TVariables, TEvent>
): MutationAnalytics => analytics as unknown as MutationAnalytics

const mutationStationCodes = new WeakMap<object, string>()

const trackMutationOutcome = (
  mutation: Mutation<unknown, unknown, unknown>,
  variables: unknown,
  outcome: AnalyticsOutcome
) => {
  try {
    const analytics = mutation.meta?.analytics
    if (!analytics) return

    const event =
      typeof analytics.event === 'function' ? analytics.event(variables) : analytics.event
    const parameters =
      typeof analytics.parameters === 'function'
        ? analytics.parameters(variables)
        : (analytics.parameters ?? {})

    const stationCode =
      outcome === 'attempted'
        ? (parameters.station_code ?? useStationStore.getState().selectedStationCode)
        : (mutationStationCodes.get(mutation) ??
          parameters.station_code ??
          useStationStore.getState().selectedStationCode)

    if (outcome === 'attempted') mutationStationCodes.set(mutation, stationCode)

    trackOutcome(
      event as AnalyticsOutcomeEventName,
      analytics.area as AnalyticsEventParametersFor<AnalyticsOutcomeEventName>['area'],
      outcome,
      {
        ...parameters,
        station_code: stationCode,
        ...(outcome === 'failed' ? { failure_type: 'api' } : {}),
      } as never
    )
  } catch {
    // Analytics must not interrupt mutation execution.
  }
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: Record<string, unknown> & {
      analytics?: MutationAnalytics
      toast?: {
        error?: string
        success?: string
      }
    }
  }
}

type ApiErrorSource = 'query' | 'mutation'

export type ApiErrorReport = {
  source: ApiErrorSource
  key: unknown
  status: number
  url: string | null
  error: unknown
}

type ApiErrorReporter = (report: ApiErrorReport) => void

let apiErrorReporter: ApiErrorReporter | null = null

type UnauthorizedHandler = (report: ApiErrorReport) => void | Promise<void>

let unauthorizedHandler: UnauthorizedHandler | null = null

export const setApiErrorReporter = (reporter: ApiErrorReporter | null) => {
  apiErrorReporter = reporter
}

export const setUnauthorizedHandler = (handler: UnauthorizedHandler | null) => {
  unauthorizedHandler = handler
}

export const notifyUnauthorized = (report: ApiErrorReport) => {
  if (report.status !== 401 && report.status !== 403) {
    return
  }

  void unauthorizedHandler?.(report)
}

const getHttpResponse = (error: unknown): Response | null => {
  if (error instanceof Response) {
    return error
  }

  return null
}

const shouldInvalidateAuth = async (response: Response): Promise<boolean> => {
  if (response.status === 401) {
    return true
  }

  if (response.status !== 403) {
    return false
  }

  const wwwAuthenticate = response.headers.get('www-authenticate')
  if (wwwAuthenticate) {
    return true
  }

  let bodyText = ''

  try {
    bodyText = await response.clone().text()
  } catch {
    return false
  }

  const normalizedText = bodyText.toLowerCase()

  if (
    normalizedText.includes('not allowed to perform this action') ||
    normalizedText.includes('permission denied') ||
    normalizedText.includes('insufficient permission')
  ) {
    return false
  }

  return (
    normalizedText.includes('token') ||
    normalizedText.includes('expired') ||
    normalizedText.includes('unauthorized') ||
    normalizedText.includes('unauthenticated') ||
    normalizedText.includes('not authenticated') ||
    normalizedText.includes('invalid') ||
    normalizedText.includes('session')
  )
}

const reportServerError = async (error: unknown, source: ApiErrorSource, key: unknown) => {
  const response = getHttpResponse(error)
  if (!response) {
    return
  }

  if (await shouldInvalidateAuth(response)) {
    notifyUnauthorized({
      source,
      key,
      status: response.status,
      url: response.url || null,
      error,
    })
  }

  if (response.status !== 500 || !apiErrorReporter) {
    return
  }

  apiErrorReporter({
    source,
    key,
    status: response.status,
    url: response.url || null,
    error,
  })
}

export const createQueryClient = (queryRetry: boolean | number = 1) =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        void reportServerError(error, 'query', query.queryKey)
      },
    }),
    mutationCache: new MutationCache({
      onMutate: (variables, mutation) => trackMutationOutcome(mutation, variables, 'attempted'),
      onSuccess: (_data, variables, _context, mutation) =>
        trackMutationOutcome(mutation, variables, 'succeeded'),
      onError: async (error, variables, _context, mutation) => {
        trackMutationOutcome(mutation, variables, 'failed')
        void reportServerError(error, 'mutation', mutation.options.mutationKey)

        const fallback = mutation.meta?.toast?.error
        if (fallback) {
          toast.error((await getApiErrorMessage(error)) ?? fallback)
        }
      },
      onSettled: (_data, error, _variables, _context, mutation) => {
        const message = mutation.meta?.toast?.success
        if (!error && message) {
          toast.success(message)
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        retry: queryRetry,
        refetchOnWindowFocus: false,
      },
    },
  })

export const queryClient = createQueryClient()
